import { ChangeDetectionStrategy, Component, inject, OnInit } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import { isEmpty } from "lodash";
import { firstValueFrom, Subscription } from "rxjs";
import { RouteType } from "src/app/models/enum";
import { PilotDataFilesDto, StaffDetails } from "src/app/models/models";
import {
	ErrorSnackbarComponent,
	ErrorSnackbarData,
} from "src/app/shared/components/cbta-snackbar/error-snackbar.component";
import {
	SuccessSnackbarComponent,
	SuccessSnackbarData,
} from "src/app/shared/components/cbta-snackbar/success-snackbar.component";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { CustomValidationService } from "../../services/custom-validation.service";

@Component({
	selector: "app-upload",
	templateUrl: "./upload.component.html",
	styleUrl: "./upload.component.scss",
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class PilotDataUploadComponent implements OnInit {
	uploadForm!: UntypedFormGroup;
	selectedFiles: File[] = [];
	selectedFileName: string = "";
	isUploading: boolean = false;
	allowedFileTypes: string = ".pdf";
	maxFileSize: number = 5 * 1024 * 1024;

	private routeParamSubscription!: Subscription;
	private snackBar = inject(MatSnackBar);
	sessionMasterData = this.sharedService.sessionMasterData;
	isOnline!: boolean;

	constructor(
		private fb: UntypedFormBuilder,
		private httpService: HttpService,
		private loadingService: LoaderService,
		public sharedService: SharedService,
		private route: ActivatedRoute,
		private router: Router,
		private loaderService: LoaderService,
		private cvs: CustomValidationService,
	) {
		this.initUploadForm();
	}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.PILOT_DATA);
		this.sharedService.getIsStatusOnline().subscribe((status: boolean) => {
			this.isOnline = status;
		});
		this.handleQueryParams();
	}

	initUploadForm(): void {
		this.uploadForm = this.fb.group({
			staffId: ["", [Validators.required]],
			name: ["", [Validators.required]],
			folderCategory: ["", [Validators.required]],
			files: [[], [Validators.required]],
		});
	}

	handleQueryParams(): void {
		this.routeParamSubscription = this.route.queryParams.subscribe((params) => {
			if (params["staffNumber"]) {
				this.uploadForm.patchValue({
					staffId: params["staffNumber"],
					name: params["name"] || "",
				});
				this.staffId.markAsTouched();
				this.name.markAsTouched();
			}
		});
	}

	ngOnDestroy(): void {
		if (this.routeParamSubscription) {
			this.routeParamSubscription.unsubscribe();
		}
	}

	get staffId() {
		return this.uploadForm.get("staffId")!;
	}

	get name() {
		return this.uploadForm.get("name")!;
	}

	get folderCategory() {
		return this.uploadForm.get("folderCategory")!;
	}

	get files() {
		return this.uploadForm.get("files")!;
	}

	get folderCategories() {
		return this.sessionMasterData?.documentTypes || [];
	}

	get selectedFilesDisplay(): string {
		return this.selectedFiles && this.selectedFiles.length > 0 ? this.selectedFiles.map((f) => f.name).join("\n") : "";
	}

	onFocusOut(event: any): void {
		this.uploadForm.markAsTouched();
		this.isOnline = navigator.onLine;
		this.cvs.validateSingleStaffId(this.staffId);

		if (this.staffId.valid && this.staffId.value.length === 7) {
			if (!navigator.onLine) return;

			this.loadingService.setLoading(true);
			this.httpService.getUserByStaffNumber(this.staffId.value).subscribe({
				next: this.onSuccessStaffId.bind(this),
				error: this.onErrorStaffId.bind(this),
			});
		}
	}

	onSuccessStaffId(resp: { data: StaffDetails }): void {
		const staffDetail = resp.data;

		if (isEmpty(this.name.value)) {
			this.name.patchValue(staffDetail.name);
		}

		this.uploadForm.markAsTouched();
		this.uploadForm.updateValueAndValidity();
		this.loadingService.setLoading(false);
	}

	onErrorStaffId(resp: any): void {
		this.loadingService.setLoading(false);

		if (resp.error?.message?.includes("No profile found") || resp.message?.includes("No profile found")) {
			this.name.patchValue("");
			this.staffId?.setErrors({ invalidStaffId: true });
			this.showErrorSnackbar("Staff ID not found. Please enter a valid Staff ID.");
		} else {
			this.showErrorSnackbar("Error retrieving staff details. Please try again.");
		}
	}

	onFileSelected(event: any): void {
		const fileList = event.target.files;

		if (!fileList || fileList.length === 0) return;

		const files = Array.from(fileList) as File[];

		const validFiles: File[] = [];

		for (const file of files) {
			// Validate each file
			if (!this.isValidFileType(file)) {
				this.showErrorSnackbar("Error! File upload has failed due to 'Invalid file type'. Please try again.");
				continue;
			}

			//handle by backend
			// if (!this.isValidFileSize(file)) {
			// 	this.showErrorSnackbar(
			// 		"Error! File upload has failed due to 'File size too large exceeding 5MB'. Please try again.",
			// 	);
			// 	continue;
			// }

			// Check for duplicates
			if (this.selectedFiles.some((existing) => existing.name === file.name)) {
				this.showErrorSnackbar(`File '${file.name}' already selected.`);
				continue;
			}

			validFiles.push(file);
		}

		this.selectedFiles.push(...validFiles);
		this.updateFormFiles();

		event.target.value = "";
	}

	removeFile(index: number): void {
		if (Array.isArray(this.selectedFiles) && index >= 0 && index < this.selectedFiles.length) {
			this.selectedFiles.splice(index, 1);
			this.updateFormFiles();
		}
	}

	clearAllFiles(): void {
		this.selectedFiles = [];
		this.updateFormFiles();
	}

	private updateFormFiles(): void {
		this.files.setValue(this.selectedFiles);
		this.files.updateValueAndValidity();
	}

	isValidFileType(file: File): boolean {
		const validTypes = [
			"application/pdf",
			"application/x-pdf",
			"application/acrobat",
			"applications/vnd.pdf",
			"text/pdf",
			"text/x-pdf",
		];
		const fileName = file.name.toLowerCase();
		const hasValidExtension = fileName.endsWith(".pdf");

		return validTypes.includes(file.type) || hasValidExtension;
	}

	isValidFileSize(file: File): boolean {
		return file.size <= this.maxFileSize;
	}

	formatFileSize(bytes: number): string {
		if (bytes === 0) return "0 Bytes";
		const k = 1024;
		const sizes = ["Bytes", "KB", "MB", "GB"];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
	}

	async onSubmit(): Promise<void> {
		this.uploadForm.updateValueAndValidity();
		this.uploadForm.markAllAsTouched();

		if (this.uploadForm.valid) {
			const formValue = this.uploadForm.value;

			this.isUploading = true;
			this.loadingService.setLoading(true);

			try {
				const uploadPromises = this.selectedFiles.map((file, index) => {
					const dto: PilotDataFilesDto = {
						staffNumber: formValue.staffId,
						file: file,
						category: formValue.folderCategory,
						filename: file.name,
					};

					return firstValueFrom(this.httpService.pilotDataWithFiles(dto));
				});

				const results = await Promise.all(uploadPromises);
				this.onSuccessUpload(results);
			} catch (error) {
				this.onErrorUpload(error);
			}
		}
	}

	onSuccessUpload(results: any[]): void {
		this.isUploading = false;
		this.loadingService.setLoading(false);

		const staffID = this.uploadForm.get("staffId")?.value;
		const staffName = this.uploadForm.get("name")?.value;
		const folderCategory = this.uploadForm.get("folderCategory")?.value;

		this.snackBar.openFromComponent(SuccessSnackbarComponent, {
			data: {
				userName: staffName,
				action: "upload",
				staffNumber: staffID,
				folderCategory: folderCategory,
			} as SuccessSnackbarData,
			duration: 4000,
			horizontalPosition: "center",
			verticalPosition: "top",
			panelClass: ["success-snackbar"],
		});

		setTimeout(() => {
			this.router.navigate([RouteType.PILOT_DATA]);
		}, 2000);
	}

	onErrorUpload(error: any): void {
		this.isUploading = false;
		this.loadingService.setLoading(false);

		let errorMessage = "Error! Failed to upload file. Please try again.";

		if (error?.error?.message) {
			errorMessage = error.error.message;
		}

		this.showErrorSnackbar(errorMessage);
	}

	private showErrorSnackbar(message: string): void {
		this.snackBar.openFromComponent(ErrorSnackbarComponent, {
			data: { message } as ErrorSnackbarData,
			duration: 5000,
			horizontalPosition: "center",
			verticalPosition: "top",
			panelClass: ["error-snackbar"],
		});
	}

	goBack(): void {
		this.router.navigate([RouteType.PILOT_DATA]);
	}
}
